// Page
import React, { useState, useRef, useCallback } from 'react';
import { Container, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const DRAG_THRESHOLD = 5;

export const Content = ({ menusWidth, pageWidth, padding = '0 10px 10px 10px', appType = 'admin', children }) => {
    const theme = useTheme();
    const containerRef = useRef(null);
    const dragStartRef = useRef(null);
    const [isDragScrolling, setIsDragScrolling] = useState(false);

    const headerHeight = appType === 'admin' ? '96px' : '0px';
    const paddingParts = padding.split(' ');
    const bottomPadding = paddingParts.length >= 3 ? paddingParts[2] : paddingParts[1] || '10px';
    const isAdmin = appType === 'admin';

    const handleDragMove = useCallback((e) => {
        if (e.buttons !== 1 || !dragStartRef.current || !containerRef.current) return;
        const scrollEl = containerRef.current;
        const startScrollLeft = dragStartRef.current.scrollLeft ?? scrollEl.scrollLeft;
        const startX = dragStartRef.current.x;
        scrollEl.scrollLeft = Math.max(0, startScrollLeft + (startX - e.clientX));
    }, []);

    const handleDragEnd = useCallback(() => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        dragStartRef.current = null;
        setIsDragScrolling(false);
    }, [handleDragMove]);

    const handleMouseDown = (e) => {
        if (e.button === 0) dragStartRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
        if (e.buttons !== 1 || !dragStartRef.current || !containerRef.current) return;
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;
        if (!isDragScrolling) {
            if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
                window.getSelection()?.removeAllRanges();
                dragStartRef.current.scrollLeft = containerRef.current.scrollLeft;
                dragStartRef.current.scrollTop = containerRef.current.scrollTop;
                setIsDragScrolling(true);
                document.addEventListener('mousemove', handleDragMove);
                document.addEventListener('mouseup', handleDragEnd);
            } else return;
        } else {
            handleDragMove(e);
        }
    };

    const handleMouseUp = () => handleDragEnd();
    const handleMouseLeave = () => {
        if (!isDragScrolling) dragStartRef.current = null;
    };

    return (
        <Container
            ref={containerRef}
            maxWidth="true"
            data-content-scroll
            sx={{
                marginLeft: `${menusWidth}px`,
                width: menusWidth ? `${pageWidth}px` : '100%',
                height: `calc(100dvh - ${headerHeight} - env(safe-area-inset-bottom, 0px))`,
                minHeight: `calc(100dvh - ${headerHeight} - env(safe-area-inset-bottom, 0px))`,
                maxHeight: `calc(100dvh - ${headerHeight} - env(safe-area-inset-bottom, 0px))`,
                padding: padding,
                paddingBottom: `calc(${bottomPadding} + env(safe-area-inset-bottom, 0px))`,
                backgroundColor: theme.palette.figma.mainBg,
                ...(isAdmin
                    ? {
                        overflowX: 'auto',
                        overflowY: 'auto',
                        overflowAnchor: 'none',
                        WebkitOverflowScrolling: 'touch',
                        display: 'block',
                        minWidth: 0,
                        minHeight: 0,
                        ...(isDragScrolling ? { userSelect: 'none', cursor: 'grabbing' } : { cursor: 'grab' }),
                    }
                    : {
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                    }),
            }}
            {...(isAdmin ? {
                onMouseDown: handleMouseDown,
                onMouseMove: handleMouseMove,
                onMouseUp: handleMouseUp,
                onMouseLeave: handleMouseLeave,
            } : {})}
        >
            {isAdmin ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 'max-content',
                        minHeight: '100%',
                    }}
                >
                    {children}
                </Box>
            ) : (
                children
            )}
        </Container>
    );
};
